// Funciones utilitarias para normalizar roles y calcular permisos relacionados con la creación de subcategorías

/**
 * Normaliza un valor de rol en bruto a una forma canónica.
 * Devuelve uno de los siguientes: 'ADMIN', 'N1', 'N2', 'N3', 'N4', 'N5' o null si es desconocido.
 * Acepta cadenas como 'Administrador', 'Administrator', o códigos de rol como
 * 'roleHibouDigitalVaultAdministrator', 'roleHibouDigitalVaultN1', etc.
 */
export function normalizeRole(raw) {
    if (raw === undefined || raw === null || raw === '') return null
    const text = String(raw).trim().toUpperCase()

    // Alias comunes para administrador
    if (
        text === 'ADMIN' ||
        text === 'ADMINISTRADOR' ||
        text === 'ADMINISTRATOR' ||
        text.includes('ADMIN') ||
        text === 'ROLEHIBOUDIGITALVAULTADMINISTRATOR'
    ) {
        return 'ADMIN'
    }

    // Códigos de niveles de Hibou Digital Vault
    const hibouMap = {
        ROLEHIBOUDIGITALVAULTN1: 'N1',
        ROLEHIBOUDIGITALVAULTN2: 'N2',
        ROLEHIBOUDIGITALVAULTN3: 'N3',
        ROLEHIBOUDIGITALVAULTN4: 'N4',
        ROLEHIBOUDIGITALVAULTN5: 'N5',
    }
    if (hibouMap[text]) return hibouMap[text]

    // Formatos genéricos de nivel N (N1..N5)
    if (/^N[1-5]$/.test(text)) return text

    return null
}

/**
 * Intenta leer el valor de rol desde un objeto de empresa usando claves comunes.
 * Devuelve un rol normalizado o null si no se encuentra.
 */
export function extractRoleFromCompany(company) {
    if (!company || typeof company !== 'object') return null

    // En muchos lugares la empresa está anidada bajo la propiedad `company`; se soportan ambas estructuras
    const source = company.company && typeof company.company === 'object' ? company.company : company

    const roleKeys = [
        'userType', 'role', 'roleCode', 'roleName', 'nivel', 'type', 'typeRole', 'userRole', 'rol', 'user_type'
    ]

    for (const key of roleKeys) {
        const value = source?.[key]
        if (value !== undefined && value !== null && value !== '') {
            const normalized = normalizeRole(value)
            if (normalized) return normalized
        }
    }

    return null
}

/**
 * Determina si se permite crear una subcategoría para un rol o empresa determinado.
 * - Admin y N1/N2/N3 => true
 * - N4/N5 => false
 * - Desconocido => defaultAllow (true por defecto para compatibilidad hacia atrás)
 */
export function canCreateSubcategory(input, { defaultAllow = true } = {}) {
    let role = null

    if (typeof input === 'string') {
        role = normalizeRole(input)
    } else if (input && typeof input === 'object') {
        role = extractRoleFromCompany(input)
    }

    if (!role) return defaultAllow
    if (role === 'ADMIN') return true
    if (role === 'N1' || role === 'N2' || role === 'N3') return true
    if (role === 'N4' || role === 'N5') return false
    return defaultAllow
}

/**
 * Función auxiliar: permite decidir si se puede crear una subcategoría
 * basándose en un objeto de empresa, con comportamiento de respaldo explícito.
 */
export function canCreateSubcategoryByCompany(company, opts) {
    return canCreateSubcategory(company, opts)
}
